import React, { useEffect, useMemo, useRef, useState } from "react";
import type {
  CampaignSummary,
  CampaignCharacter,
  CampaignSettings,
  NoChatScenario,
  NoChatTemplate,
  PremiumCriteria,
  PromptTemplate,
  SendSchedule,
  SendTimeOption,
  ThreadFollowUpSettings,
} from "../types";
import { DEFAULT_SEND_TIME_VALUE } from "../types";
import { SectionCard } from "./SectionLayout";
import { SequenceEditor } from "./SequenceEditor";
import PremiumConfigurationSection from "./PremiumConfigurationSection";
import { NoChatScenariosEditor } from "./NoChatScenariosEditor";
import { getCharacterTierMeta } from "../utils/characterTier";
import {
  DuplicateCampaignModal,
  type DuplicateCampaignPayload,
} from "./DuplicateCampaignModal";

interface SettingsSectionProps {
  settings: CampaignSettings;
  sendTimeOptions: SendTimeOption[];
  premiumCriteria: PremiumCriteria;
  noChatSchedule?: SendSchedule;
  noChatTemplate: NoChatTemplate;
  premiumPrompt: PromptTemplate;
  characters: CampaignCharacter[];
  campaigns: CampaignSummary[];
  selectedCampaignId: string;
  liveActiveCampaignId?: string;
  onSelectCampaign: (campaignId: string) => void;
  onCreateCampaign: (name: string) => void;
  onDuplicateCampaign?: (
    payload: DuplicateCampaignPayload
  ) => Promise<void> | void;
  isDuplicatingCampaign?: boolean;
  onActivateCampaign: () => void;
  onDeactivateCampaign: () => void;
  onDeleteCampaign: () => Promise<void> | void;
  onSaveSequence?: () => void;
  isSavingSequence?: boolean;
  onSaveCharacterSelection: () => void;
  isSavingCharacterSelection?: boolean;
  onSaveCharacterReplyTiming: () => void;
  isSavingCharacterReplyTiming?: boolean;
  onSaveNoChatConfiguration: () => void;
  isSavingNoChatConfiguration?: boolean;
  onSaveReachOutRules: () => void;
  isSavingReachOutRules?: boolean;
  onChange: (settings: CampaignSettings) => void;
  onUpdatePremium: (criteria: PremiumCriteria) => void;
  onUpdateNoChatSchedule: (schedule: SendSchedule) => void;
  onUpdateNoChatTemplate: (template: NoChatTemplate) => void;
  onUpdatePremiumPrompt: (prompt: PromptTemplate) => void;
}

export const SettingsSection: React.FC<SettingsSectionProps> = ({
  settings,
  sendTimeOptions,
  premiumCriteria,
  noChatSchedule,
  noChatTemplate,
  premiumPrompt,
  characters,
  campaigns,
  selectedCampaignId,
  liveActiveCampaignId,
  onSelectCampaign,
  onCreateCampaign,
  onDuplicateCampaign,
  isDuplicatingCampaign,
  onActivateCampaign,
  onDeactivateCampaign,
  onDeleteCampaign,
  onSaveSequence,
  isSavingSequence,
  onSaveCharacterSelection,
  isSavingCharacterSelection,
  onSaveCharacterReplyTiming,
  isSavingCharacterReplyTiming,
  onSaveNoChatConfiguration,
  isSavingNoChatConfiguration,
  onSaveReachOutRules,
  isSavingReachOutRules,
  onChange,
  onUpdatePremium,
  onUpdateNoChatSchedule,
  onUpdateNoChatTemplate,
  onUpdatePremiumPrompt,
}) => {
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const campaignSelectRef = useRef<HTMLSelectElement | null>(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const updateSettings = (
    updater: (current: CampaignSettings) => CampaignSettings
  ) => {
    onChange(updater(settings));
  };

  const schedule: SendSchedule = noChatSchedule ?? {
    offsetValue: 24,
    offsetUnit: "hours",
    sendTimeOptionId: DEFAULT_SEND_TIME_VALUE,
  };

  const ensureDynamicRange = (
    value: ThreadFollowUpSettings
  ): ThreadFollowUpSettings["dynamicRange"] => {
    const range = value.dynamicRange || { minTime: 0, maxTime: 0, unit: "hours" };
    const unit = range.unit === "minutes" ? "minutes" : "hours";
    const minCandidate =
      Number.isFinite((range as any).minTime) && typeof (range as any).minTime === "number"
        ? (range as any).minTime
        : Number.isFinite((range as any).minHours)
        ? (range as any).minHours
        : 0;
    const maxCandidate =
      Number.isFinite((range as any).maxTime) && typeof (range as any).maxTime === "number"
        ? (range as any).maxTime
        : Number.isFinite((range as any).maxHours)
        ? (range as any).maxHours
        : minCandidate;

    const minTime = Math.max(0, minCandidate);
    const maxTime = Math.max(minTime, maxCandidate);

    return {
      minTime,
      maxTime,
      unit,
    };
  };

  const dynamicRange = ensureDynamicRange(settings.threadFollowUp);

  const handleDynamicRangeChange = (
    updates: Partial<{
      minTime?: number;
      maxTime?: number;
    }>
  ) => {
    updateSettings((current) => {
      const range = ensureDynamicRange(current.threadFollowUp);
      const nextMin =
        updates.minTime !== undefined
          ? Math.max(0, updates.minTime)
          : range.minTime;
      const nextMaxCandidate =
        updates.maxTime !== undefined
          ? updates.maxTime
          : range.maxTime;
      const nextMax = Math.max(nextMin, nextMaxCandidate);

      return {
        ...current,
        threadFollowUp: {
          dynamicRange: {
            ...range,
            minTime: nextMin,
            maxTime: nextMax,
          },
        },
      };
    });
  };

  const handleDynamicRangeUnitChange = (
    unit: ThreadFollowUpSettings["dynamicRange"]["unit"]
  ) => {
    updateSettings((current) => {
      const range = ensureDynamicRange(current.threadFollowUp);
      if (range.unit === unit) {
        return current;
      }
      const convert = (value: number, fromUnit: string, toUnit: string) => {
        if (fromUnit === toUnit) return value;
        return fromUnit === "hours" ? value * 60 : value / 60;
      };
      const convertedMin = convert(range.minTime, range.unit, unit);
      const convertedMax = convert(range.maxTime, range.unit, unit);
      const nextMin = Math.max(0, convertedMin);
      const nextMax = Math.max(nextMin, convertedMax);

      return {
        ...current,
        threadFollowUp: {
          dynamicRange: {
            minTime: nextMin,
            maxTime: nextMax,
            unit,
          },
        },
      };
    });
  };
  const defaultCharacterValue =
    settings.defaultCharacterId || characters[0]?.id || "";
  const defaultCharacter = useMemo(
    () => characters.find((character) => character.id === defaultCharacterValue),
    [characters, defaultCharacterValue]
  );

  useEffect(() => {
    if (!settings.defaultCharacterId && characters.length > 0) {
      updateSettings((current) => ({
        ...current,
        defaultCharacterId: characters[0]?.id ?? "",
      }));
    }
  }, [characters, settings.defaultCharacterId]);
  const noChatScenarios = noChatTemplate.triggerScenarios ?? [];
  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => campaign.id === selectedCampaignId),
    [campaigns, selectedCampaignId]
  );
  const selectedCampaignName = selectedCampaign?.name ?? "Untitled campaign";
  const duplicateNameSuggestion = useMemo(
    () =>
      selectedCampaign?.name
        ? `${selectedCampaign.name} copy`
        : "New campaign copy",
    [selectedCampaign]
  );
  const isSelectedActive = selectedCampaign?.status === "active";
  const liveCampaignSummary = useMemo(
    () => campaigns.find((campaign) => campaign.id === liveActiveCampaignId),
    [campaigns, liveActiveCampaignId]
  );

  const handleCreateCampaign = () => {
    const trimmedName = newCampaignName.trim();
    if (trimmedName.length === 0) {
      return;
    }
    onCreateCampaign(trimmedName);
    setNewCampaignName("");
    setIsCreatingCampaign(false);
  };

  const handleNoChatScenarioChange = (scenarios: NoChatScenario[]) => {
    onUpdateNoChatTemplate({
      ...noChatTemplate,
      triggerScenarios: scenarios,
      lastUpdated: new Date().toISOString(),
    });
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;
    setIsDeleting(true);
    try {
      await onDeleteCampaign();
      setShowDeleteConfirmation(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenDuplicateModal = () => {
    if (!campaigns.length) return;
    setIsDuplicateModalOpen(true);
  };

  const handleConfirmDuplicate = async (
    payload: DuplicateCampaignPayload
  ) => {
    if (!onDuplicateCampaign) {
      setIsDuplicateModalOpen(false);
      return;
    }
    try {
      await onDuplicateCampaign(payload);
      setIsDuplicateModalOpen(false);
    } catch (error) {
      console.error("Failed to duplicate campaign", error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Campaign settings
              </p>
              <h2 className="text-2xl font-semibold text-gray-900">Editing campaign</h2>
              <p className="text-sm text-gray-500">
                Switch to another campaign to edit its automation settings. Use the actions
                below to activate or retire a campaign.
              </p>
            </div>
            {selectedCampaign ? (
              <span
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-wide ${
                  isSelectedActive
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700"
                }`}
              >
                <span className="h-2 w-2 rounded-full bg-current"></span>
                {isSelectedActive ? "Live campaign" : "Draft campaign"}
              </span>
            ) : (
              <span className="rounded-full bg-gray-100 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Select a campaign
              </span>
            )}
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-2 rounded-2xl border border-gray-100 bg-gray-50/80 p-4 text-xs font-semibold uppercase tracking-wide text-gray-600">
              Switch campaign
              <select
                ref={campaignSelectRef}
                value={selectedCampaignId}
                onChange={(event) => onSelectCampaign(event.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                    {campaign.status === "active" ? " (Live)" : ""}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">
                Currently editing: {selectedCampaignName}
              </span>
            </label>
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-4 text-sm text-gray-700 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Another one live
              </p>
              <p className="mt-1">
                {liveCampaignSummary
                  ? `“${liveCampaignSummary.name}” is live right now.`
                  : "No other campaign is active at the moment."}
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Switching lets you fine-tune its automation rules without impacting drafts.
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl bg-gray-50 p-4 h-full">
              <div className="flex h-full flex-col justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold text-gray-900">Create a new campaign</p>
                  <p className="text-xs text-gray-500">
                    Creating a campaign copies your default settings so you can tune a fresh
                    variant.
                  </p>
                  {isCreatingCampaign ? (
                    <div className="mt-2 flex flex-col gap-3">
                      <input
                        type="text"
                        autoFocus
                        value={newCampaignName}
                        onChange={(event) => setNewCampaignName(event.target.value)}
                        placeholder="Campaign name"
                        className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                      />
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {isCreatingCampaign ? (
                    <>
                      <button
                        type="button"
                        onClick={handleCreateCampaign}
                        disabled={newCampaignName.trim().length === 0}
                        className="rounded-full bg-[var(--primary-color)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm transition hover:bg-[var(--primary-color)]/90 disabled:opacity-50 min-w-[150px]"
                      >
                        Create
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreatingCampaign(false);
                          setNewCampaignName("");
                        }}
                        className="rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700 transition hover:border-gray-300 min-w-[120px]"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsCreatingCampaign(true)}
                      className="rounded-full bg-[var(--primary-color)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm transition hover:bg-[var(--primary-color)]/90 min-w-[160px]"
                    >
                      New campaign
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-4 text-sm text-gray-700 shadow-sm h-full">
              <div className="flex h-full flex-col justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold text-gray-900">Duplicate a campaign</p>
                  <p className="text-xs text-gray-500">
                    Open a quick picker to clone an existing draft or live campaign with the parts you need.
                  </p>
                </div>
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={handleOpenDuplicateModal}
                    disabled={!campaigns.length}
                    className="rounded-full bg-[var(--primary-color)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm transition hover:bg-[var(--primary-color)]/90 disabled:cursor-not-allowed disabled:opacity-60 min-w-[170px] text-center"
                  >
                    Duplicate campaign
                  </button>
                </div>
              </div>
            </div>
          </div>

          {selectedCampaign ? (
            <div className="mt-6 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Campaign actions
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={isSelectedActive ? onDeactivateCampaign : onActivateCampaign}
                  className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide ${
                    isSelectedActive
                      ? "border border-gray-200 text-gray-700 hover:border-gray-300"
                      : "bg-[var(--primary-color)] text-white hover:bg-[var(--primary-color)]/90"
                  }`}
                >
                  {isSelectedActive ? "Deactivate campaign" : "Activate campaign"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation((current) => !current)}
                  className="rounded-full border border-red-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-red-600 hover:bg-red-50"
                >
                  Delete campaign
                </button>
              </div>
            </div>
          ) : null}

          {showDeleteConfirmation && selectedCampaign ? (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
              <div className="font-semibold">
                Permanently delete “{selectedCampaign.name}”?
              </div>
              <p className="mt-1 text-red-800">
                This action cannot be undone and will remove all configuration data for
                this campaign.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleDeleteCampaign}
                  disabled={isDeleting}
                  className="rounded-xl bg-red-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-60"
                >
                  {isDeleting ? "Deleting..." : "Delete permanently"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="rounded-xl border border-red-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-red-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>

      <SectionCard
        title="Reengagement Email Sequence"
        description="Define when emails are sent after a user goes inactive."
        collapsible
        defaultOpen={false}
      >
        <SequenceEditor
          sequence={settings.sequence}
          sendTimeOptions={sendTimeOptions}
          onSave={onSaveSequence}
          isSaving={isSavingSequence}
          onChange={(sequence) =>
            updateSettings((current) => ({ ...current, sequence }))
          }
        />
      </SectionCard>

      <SectionCard
        title="Reengagement Character Selection Rules"
        description="Fine-tune how we pick the character that sends each follow-up after a user goes quiet."
        collapsible
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Token threshold for character selection
            </span>
            <div className="relative rounded-xl border border-gray-200 bg-white px-4 py-2.5">
              <input
                type="number"
                min={0}
                step={1000}
                value={settings.tokenThreshold}
                onChange={(event) =>
                  updateSettings((current) => ({
                    ...current,
                    tokenThreshold: Number(event.target.value),
                  }))
                }
                className="w-full border-none text-sm focus:outline-none focus:ring-0"
              />
              <span className="absolute inset-y-0 right-4 flex items-center text-xs font-medium text-gray-500 uppercase">
                tokens
              </span>
            </div>
            <span className="text-xs text-gray-500">
              Users must cross this spend for a character to be considered.
            </span>
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Character selection strategy
            </span>
            <select
              value={settings.characterStrategy}
              onChange={(event) =>
                updateSettings((current) => ({
                  ...current,
                  characterStrategy: event.target
                    .value as CampaignSettings["characterStrategy"],
                }))
              }
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            >
              <option value="last-high-token">
                Last character with ≥ threshold tokens
              </option>
              <option value="most-frequent">
                Character with most conversations
              </option>
            </select>
            <span className="text-xs text-gray-500">
              Choose how to determine the sender for each follow-up.
            </span>
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Default character fallback
            </span>
            <div className="relative">
              <select
                value={defaultCharacterValue}
                onChange={(event) =>
                  updateSettings((current) => ({
                    ...current,
                    defaultCharacterId: event.target.value,
                  }))
                }
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-12 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                {characters.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-xs text-gray-500">
              Used when no past character meets the rules. Defaults to the
              first character in your list.
            </span>
          </label>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onSaveCharacterSelection}
            disabled={isSavingCharacterSelection}
            className="rounded-xl bg-[var(--primary-color)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-60"
          >
            {isSavingCharacterSelection ? "Saving..." : "Save character rules"}
          </button>
        </div>
      </SectionCard>

      <SectionCard
        title="Character Reply Timing"
        description="Set the delay before we send another email to someone who replied directly to a character message. If they jump back into the chat UI instead, the regular character conversation takes over."
        collapsible
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            Randomize the wait window
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <label className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Earliest follow-up ({dynamicRange.unit})
              </span>
              <input
                type="number"
                min={0}
                step={dynamicRange.unit === "minutes" ? 1 : 0.5}
                value={dynamicRange.minTime}
                onChange={(event) =>
                  handleDynamicRangeChange({
                    minTime: parseFloat(event.target.value),
                  })
                }
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              />
              <span className="text-xs text-gray-500">
                Set to 0 if you want instant replies to be possible.
              </span>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Latest follow-up ({dynamicRange.unit})
              </span>
              <input
                type="number"
                min={dynamicRange.minTime}
                step={dynamicRange.unit === "minutes" ? 1 : 0.5}
                value={dynamicRange.maxTime}
                onChange={(event) =>
                  handleDynamicRangeChange({
                    maxTime: parseFloat(event.target.value),
                  })
                }
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              />
              <span className="text-xs text-gray-500">
                We&apos;ll never wait longer than this before sending the next message.
              </span>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Time unit
              </span>
              <select
                value={dynamicRange.unit}
                onChange={(event) =>
                  handleDynamicRangeUnitChange(
                    event.target.value === "minutes" ? "minutes" : "hours"
                  )
                }
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
              </select>
              <span className="text-xs text-gray-500">
                Use minutes for shorter reply windows (e.g., 20-minute replies).
              </span>
            </label>
          </div>
          <div className="text-xs text-gray-500">
            Each time a user responds, the system randomly picks a delay between
            these two values so follow-ups feel delightfully unpredictable. Set
            both to the same number for a consistent send time.
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onSaveCharacterReplyTiming}
            disabled={isSavingCharacterReplyTiming}
            className="rounded-xl bg-[var(--primary-color)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-60"
          >
            {isSavingCharacterReplyTiming ? "Saving..." : "Save reply timing"}
          </button>
        </div>
      </SectionCard>

      <SectionCard
        title="No-Chat Introductions"
        description="Control when the first outreach email sends to users who never start a conversation."
        collapsible
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:max-w-4xl">
          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Delay after signup
            </span>
            <input
              type="number"
              min={0}
              value={schedule.offsetValue}
              onChange={(event) =>
                onUpdateNoChatSchedule({
                  ...schedule,
                  offsetValue: Number(event.target.value),
                })
              }
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            />
            <span className="text-xs text-gray-500">
              How long to wait after signup before sending.
            </span>
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Units
            </span>
            <select
              value={schedule.offsetUnit}
              onChange={(event) =>
                onUpdateNoChatSchedule({
                  ...schedule,
                  offsetUnit: event.target.value as SendSchedule["offsetUnit"],
                })
              }
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </select>
            <span className="text-xs text-gray-500">
              Choose the unit for the delay.
            </span>
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Time to send
            </span>
            <select
              value={schedule.sendTimeOptionId ?? DEFAULT_SEND_TIME_VALUE}
              onChange={(event) =>
                onUpdateNoChatSchedule({
                  ...schedule,
                  sendTimeOptionId: event.target.value,
                })
              }
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            >
              <option value={DEFAULT_SEND_TIME_VALUE}>Use default timing</option>
              {sendTimeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-500">
              Align send time to the user&apos;s timezone.
            </span>
          </label>
        </div>

        <div className="mt-6">
          <NoChatScenariosEditor
            scenarios={noChatScenarios}
            characterOptions={characters.map(({ id, name, tier }) => ({
              id,
              name,
              tier,
            }))}
            onChange={handleNoChatScenarioChange}
          />
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onSaveNoChatConfiguration}
            disabled={isSavingNoChatConfiguration}
            className="rounded-xl bg-[var(--primary-color)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-60"
          >
            {isSavingNoChatConfiguration ? "Saving..." : "Save no-chat intro"}
          </button>
        </div>
      </SectionCard>

      <PremiumConfigurationSection
        criteria={premiumCriteria}
        prompt={premiumPrompt}
        characters={characters}
        onUpdate={onUpdatePremium}
        onUpdatePrompt={onUpdatePremiumPrompt}
        sendTimeOptions={sendTimeOptions}
        onSave={onSaveReachOutRules}
        isSaving={isSavingReachOutRules}
      />
      <DuplicateCampaignModal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        onConfirm={handleConfirmDuplicate}
        campaignOptions={campaigns}
        defaultSourceCampaignId={selectedCampaignId}
        defaultName={duplicateNameSuggestion}
        isSubmitting={isDuplicatingCampaign}
      />
    </div>
  );
};
